import { Box, keyframes } from "@mui/material";
import { FC } from "react";
import { NAV_BAR_HEIGHT } from "../Components/NavBar/navBarMetrics";

/**
 * Decoration for the kawaii theme: nyan cats trailing rainbows, a string of
 * blinking fairy lights, and a steady drift of Japanese emoji.
 *
 * Two rules keep this from being a liability on a trading screen:
 *
 * 1. `pointer-events: none` throughout, so nothing here can ever swallow a
 *    click meant for an order or a stop button.
 * 2. Every animation sits behind `prefers-reduced-motion: no-preference`. With
 *    reduced motion requested the decorations still render, they just hold
 *    still.
 */

const flyAcross = keyframes`
  from { transform: translateX(-30vw); }
  to   { transform: translateX(130vw); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1;    filter: brightness(1.6); }
  50%      { opacity: 0.25; filter: brightness(0.7); }
`;

/**
 * Slow vertical drift for the fairy lights. Amplitude comes from a per-light
 * CSS variable so each one travels a different distance; pairing that with
 * per-light durations and negative delays keeps them from ever falling into
 * step with each other.
 */
const sway = keyframes`
  0%, 100% { transform: translateY(calc(var(--sway) * -1)); }
  50%      { transform: translateY(var(--sway)); }
`;

const rise = keyframes`
  from { transform: translateY(8vh) rotate(0deg);      opacity: 0; }
  10%  { opacity: 0.9; }
  90%  { opacity: 0.9; }
  to   { transform: translateY(-105vh) rotate(360deg); opacity: 0; }
`;

/** Marches the pixel notches along the trail without moving the stripes. */
const rainbowShift = keyframes`
  from { background-position: 0 0, 0 0; }
  to   { background-position: 16px 0, 0 0; }
`;

/** Only animate when the viewer has not asked for calm. */
const motionOk = "@media (prefers-reduced-motion: no-preference)";

const NYAN_CATS = [
  { top: "18%", duration: 9, delay: 0, scale: 1 },
  { top: "47%", duration: 14, delay: 3, scale: 0.7 },
  { top: "73%", duration: 11, delay: 6, scale: 0.85 },
];

const FAIRY_LIGHT_COLOURS = [
  "#FF4FD8",
  "#FFE14D",
  "#22E0FF",
  "#8BFF6B",
  "#FF5C8A",
  "#C77DFF",
];

const DRIFTING_EMOJI = [
  "🌸",
  "🍣",
  "🏮",
  "⛩️",
  "🎏",
  "🍡",
  "🦊",
  "🍥",
  "🎋",
  "🗾",
  "🎌",
  "🌈",
  "✨",
  "🍜",
  "🐱",
  "🗻",
];

/**
 * The rainbow streak trailing each cat: six horizontal stripes, with a second
 * layer of darker notches scrolling along them for the pixel-march effect. The
 * notch layer is listed first so `background-position` animates it, not the
 * stripes.
 */
const RainbowTrail: FC = () => (
  <Box
    sx={{
      width: "26vw",
      height: 30,
      borderRadius: "3px",
      backgroundImage: [
        "repeating-linear-gradient(90deg, rgba(0,0,0,0.18) 0 8px, rgba(0,0,0,0) 8px 16px)",
        "linear-gradient(#FF0018 0 16.66%, #FFA52C 16.66% 33.33%, #FFFF41 33.33% 50%, #00CC48 50% 66.66%, #0064FF 66.66% 83.33%, #86007D 83.33% 100%)",
      ].join(","),
      backgroundSize: "16px 100%, 100% 100%",
      // Fade the tail out instead of ending on a hard edge.
      maskImage: "linear-gradient(90deg, transparent, #000 35%)",
      WebkitMaskImage: "linear-gradient(90deg, transparent, #000 35%)",
      [motionOk]: {
        animation: `${rainbowShift} 0.4s linear infinite`,
      },
    }}
  />
);

const NyanCat: FC<{
  top: string;
  duration: number;
  delay: number;
  scale: number;
}> = ({ top, duration, delay, scale }) => (
  // Outer element owns the horizontal travel, inner owns the scale. Keeping
  // them apart stops the keyframes' `transform` from dropping the scale.
  <Box
    sx={{
      position: "absolute",
      top,
      left: 0,
      transform: "translateX(-30vw)",
      [motionOk]: {
        animation: `${flyAcross} ${duration}s linear ${delay}s infinite`,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        transform: `scale(${scale})`,
        transformOrigin: "left center",
      }}
    >
      <RainbowTrail />
      <Box
        component="span"
        sx={{
          fontSize: 34,
          lineHeight: 1,
          filter: "drop-shadow(0 0 8px rgba(255,79,216,0.9))",
          [motionOk]: {
            animation: `${bob} 0.5s ease-in-out infinite`,
          },
        }}
      >
        🐱
      </Box>
    </Box>
  </Box>
);

const FairyLights: FC = () => (
  <Box
    sx={{
      position: "absolute",
      // Sits far enough below the toolbar that the drift never rides up over it
      // (max amplitude is 10px).
      top: NAV_BAR_HEIGHT + 12,
      left: 0,
      right: 0,
      display: "flex",
      justifyContent: "space-around",
    }}
  >
    {Array.from({ length: 28 }).map((_, i) => {
      const colour = FAIRY_LIGHT_COLOURS[i % FAIRY_LIGHT_COLOURS.length];

      // Co-prime-ish strides over the index so amplitude, period and phase all
      // cycle at different rates. Sharing one stride would make every sixth
      // light identical.
      const amplitude = 4 + (i % 4) * 2;
      const swayDuration = 3.4 + (i % 7) * 0.53;
      // A negative delay starts each light part-way through its cycle, so they
      // are already scattered on first paint instead of setting off together.
      const swayDelay = -(i * 0.37);

      return (
        // Outer element owns the drift, inner owns the blink. Splitting them
        // keeps one `transform` animation per element.
        <Box
          key={i}
          sx={{
            "--sway": `${amplitude}px`,
            [motionOk]: {
              animation: `${sway} ${swayDuration}s ease-in-out ${swayDelay}s infinite`,
            },
          }}
        >
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              backgroundColor: colour,
              boxShadow: `0 0 10px 3px ${colour}`,
              [motionOk]: {
                animation: `${blink} ${0.7 + (i % 5) * 0.28}s ease-in-out ${
                  i * 0.09
                }s infinite`,
              },
            }}
          />
        </Box>
      );
    })}
  </Box>
);

const DriftingEmoji: FC = () => (
  <>
    {DRIFTING_EMOJI.map((emoji, i) => (
      <Box
        key={`${emoji}-${i}`}
        component="span"
        sx={{
          position: "absolute",
          bottom: "-8vh",
          left: `${(i * 100) / DRIFTING_EMOJI.length + 1}%`,
          fontSize: 20 + (i % 4) * 7,
          opacity: 0.9,
          [motionOk]: {
            animation: `${rise} ${11 + (i % 6) * 2.5}s linear ${
              i * 0.8
            }s infinite`,
          },
        }}
      >
        {emoji}
      </Box>
    ))}
  </>
);

export const KawaiiOverlay: FC = () => (
  <Box
    aria-hidden
    sx={{
      position: "fixed",
      inset: 0,
      overflow: "hidden",
      // Never intercept a click; this is scenery, and the app underneath is
      // used to place real orders.
      pointerEvents: "none",
      // Above the toolbar and panels so it is properly visible, but below
      // modals and tooltips - a nyan cat over a confirmation dialog is funny
      // once and a hazard after that.
      zIndex: (theme) => theme.zIndex.drawer - 1,
    }}
  >
    <FairyLights />
    <DriftingEmoji />
    {NYAN_CATS.map((cat) => (
      <NyanCat key={cat.top} {...cat} />
    ))}
  </Box>
);
